import { MIN_GAIN, type AudioVoice } from "../constants";
import type {
  AdvancedSynthesisInput,
  AdvancedVoiceHandle,
  AudioQualityMode,
  InstrumentSynthesisFamily,
  ModalSpec,
  ResonatorSpec
} from "./types";

export interface EnvelopeSchedule {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  peakGain: number;
  startTime?: number;
}

export interface NoiseBurstOptions {
  duration: number;
  attack?: number;
  decay?: number;
  gain?: number;
  filterType?: BiquadFilterType;
  frequency?: number;
  q?: number;
  seed?: number;
  tilt?: "bright" | "dark" | "flat";
  startTime?: number;
}

export interface CreatedNoiseBurst {
  source: AudioBufferSourceNode;
  output: AudioNode;
  gain: GainNode;
  filter?: BiquadFilterNode;
  cleanupNodes: AudioNode[];
}

export interface CreatedModalBank {
  output: GainNode;
  sources: OscillatorNode[];
  cleanupNodes: AudioNode[];
  maxDecay: number;
}

export interface CreatedKarplusStrongString {
  source: AudioBufferSourceNode;
  output: GainNode;
  delay: DelayNode;
  feedbackGain: GainNode;
  dampingFilter: BiquadFilterNode;
  exciterFilter: BiquadFilterNode;
  cleanupNodes: AudioNode[];
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function centsToRatio(cents: number): number {
  return 2 ** (cents / 1200);
}

export function ratioToCents(ratio: number): number {
  return ratio > 0 ? 1200 * Math.log2(ratio) : 0;
}

export function onePoleLowpassCoefficient(cutoffHz: number, sampleRate: number): number {
  const safeCutoff = clamp(cutoffHz, 1, sampleRate * 0.45);
  const x = Math.exp((-2 * Math.PI * safeCutoff) / sampleRate);

  return 1 - x;
}

export function seededRandom(seed: number): () => number {
  let state = Math.floor(seed) || 1;

  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;

    return state / 0xffffffff;
  };
}

export function hashSeed(...parts: Array<string | number | undefined>): number {
  const text = parts.map((part) => String(part ?? "")).join(":");
  let hash = 2166136261;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

export function clampFeedbackGain(value: number): number {
  return clamp(value, 0, 0.985);
}

export function performanceCap(
  family: InstrumentSynthesisFamily,
  quality: AudioQualityMode = "standard"
): { partialCap: number; feedbackMax: number; activeVoiceCap: number } {
  const qualityScale: Record<AudioQualityMode, number> = {
    mobile_safe: 0.65,
    standard: 1,
    high: 1.6
  };
  const basePartialCaps: Record<InstrumentSynthesisFamily, number> = {
    plucked_string_waveguide: 9,
    bowed_string_friction: 7,
    edge_tone_bore: 8,
    membrane_modal: 11,
    metal_tine_modal: 9,
    bronze_bar_modal: 10,
    lip_reed_drone: 6
  };
  const partialCap = Math.max(3, Math.round(basePartialCaps[family] * qualityScale[quality]));

  return {
    partialCap,
    feedbackMax: quality === "mobile_safe" ? 0.965 : 0.985,
    activeVoiceCap: family === "lip_reed_drone" ? 2 : quality === "mobile_safe" ? 18 : 30
  };
}

export function scheduleSafeEnvelope(gainParam: AudioParam, context: BaseAudioContext, envelope: EnvelopeSchedule): void {
  const now = envelope.startTime ?? context.currentTime;
  const attack = Math.max(0.001, envelope.attack);
  const decay = Math.max(0.001, envelope.decay);
  const peakGain = Math.max(MIN_GAIN, envelope.peakGain);
  const sustainGain = Math.max(MIN_GAIN, peakGain * clamp01(envelope.sustain));

  gainParam.cancelScheduledValues(now);
  gainParam.setValueAtTime(MIN_GAIN, now);
  gainParam.exponentialRampToValueAtTime(peakGain, now + attack);
  gainParam.setTargetAtTime(sustainGain, now + attack, decay);
}

export function schedulePercussiveEnvelope(
  gainParam: AudioParam,
  context: BaseAudioContext,
  peakGain: number,
  attack: number,
  decay: number,
  startTime = context.currentTime
): void {
  gainParam.cancelScheduledValues(startTime);
  gainParam.setValueAtTime(MIN_GAIN, startTime);
  gainParam.exponentialRampToValueAtTime(Math.max(MIN_GAIN, peakGain), startTime + Math.max(0.001, attack));
  gainParam.setTargetAtTime(MIN_GAIN, startTime + Math.max(0.001, attack), Math.max(0.002, decay));
}

export function releaseAudioParam(
  gainParam: AudioParam,
  context: BaseAudioContext,
  releaseSeconds: number,
  when = context.currentTime
): void {
  gainParam.cancelScheduledValues(when);
  gainParam.setTargetAtTime(MIN_GAIN, when, Math.max(0.002, releaseSeconds));
}

export function createNoiseBurst(context: BaseAudioContext, options: NoiseBurstOptions): CreatedNoiseBurst {
  const duration = Math.max(0.001, options.duration);
  const frameCount = Math.max(1, Math.floor(context.sampleRate * duration));
  const buffer = context.createBuffer(1, frameCount, context.sampleRate);
  const data = buffer.getChannelData(0);
  const random = seededRandom(options.seed ?? 1);
  const attackFrames = Math.max(1, Math.floor((options.attack ?? Math.min(0.002, duration * 0.25)) * context.sampleRate));
  const decayPower = options.decay ?? 2.2;

  for (let index = 0; index < frameCount; index += 1) {
    const t = index / Math.max(1, frameCount - 1);
    const attack = Math.min(1, index / attackFrames);
    const decay = (1 - t) ** decayPower;
    const tilt =
      options.tilt === "bright"
        ? 0.55 + t * 0.9
        : options.tilt === "dark"
          ? 1.1 - t * 0.6
          : 1;

    data[index] = (random() * 2 - 1) * attack * decay * tilt;
  }

  const source = context.createBufferSource();
  const gain = context.createGain();
  let output: AudioNode = gain;
  const cleanupNodes: AudioNode[] = [gain];
  let filter: BiquadFilterNode | undefined;
  const startTime = options.startTime ?? context.currentTime;

  source.buffer = buffer;
  gain.gain.setValueAtTime(Math.max(MIN_GAIN, options.gain ?? 0.08), startTime);

  if (options.frequency) {
    filter = context.createBiquadFilter();
    filter.type = options.filterType ?? "bandpass";
    filter.frequency.setValueAtTime(Math.max(20, options.frequency), startTime);
    filter.Q.setValueAtTime(Math.max(0.0001, options.q ?? 1), startTime);
    source.connect(filter);
    filter.connect(gain);
    cleanupNodes.push(filter);
  } else {
    source.connect(gain);
  }

  output = gain;

  return { source, output, gain, filter, cleanupNodes };
}

export function createModalBank(
  context: BaseAudioContext,
  baseFrequency: number,
  modes: ModalSpec[],
  options: {
    destination?: AudioNode;
    velocity: number;
    now?: number;
    family: InstrumentSynthesisFamily;
    quality?: AudioQualityMode;
    totalGain?: number;
    duration?: number;
  }
): CreatedModalBank {
  const now = options.now ?? context.currentTime;
  const cap = performanceCap(options.family, options.quality).partialCap;
  const output = context.createGain();
  const sources: OscillatorNode[] = [];
  const cleanupNodes: AudioNode[] = [output];
  let maxDecay = 0;

  output.gain.value = Math.max(MIN_GAIN, options.totalGain ?? 1);
  output.connect(options.destination ?? context.destination);

  for (const mode of modes.slice(0, cap)) {
    const source = context.createOscillator();
    const gain = context.createGain();
    const frequency = Math.max(20, baseFrequency * mode.ratio * centsToRatio(mode.detuneCents ?? 0));
    const decay = Math.max(0.015, mode.decay);

    source.type = mode.type ?? "sine";
    source.frequency.setValueAtTime(frequency, now);
    gain.gain.setValueAtTime(MIN_GAIN, now);
    gain.gain.exponentialRampToValueAtTime(
      Math.max(MIN_GAIN, options.velocity * mode.gain),
      now + 0.002
    );
    gain.gain.setTargetAtTime(MIN_GAIN, now + 0.006, decay);
    source.connect(gain);
    gain.connect(output);
    source.start(now);
    source.stop(now + Math.max(options.duration ?? 0, decay * 7, 0.12));
    sources.push(source);
    cleanupNodes.push(gain);
    maxDecay = Math.max(maxDecay, decay);
  }

  return { output, sources, cleanupNodes, maxDecay };
}

export function createBodyResonatorBank(
  context: BaseAudioContext,
  input: AudioNode,
  destination: AudioNode,
  resonators: ResonatorSpec[],
  wetGain = 0.55
): AudioNode[] {
  const wet = context.createGain();
  const cleanupNodes: AudioNode[] = [wet];

  wet.gain.value = Math.max(MIN_GAIN, wetGain);
  wet.connect(destination);

  for (const resonator of resonators) {
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    filter.type = resonator.type ?? "peaking";
    filter.frequency.value = Math.max(20, resonator.frequency);
    filter.Q.value = Math.max(0.001, resonator.q);
    filter.gain.value = resonator.gainDb;
    gain.gain.value = 1 / Math.max(1, resonators.length);
    input.connect(filter);
    filter.connect(gain);
    gain.connect(wet);
    cleanupNodes.push(filter, gain);
  }

  return cleanupNodes;
}

export function createKarplusStrongString(
  context: BaseAudioContext,
  input: AdvancedSynthesisInput,
  options: {
    frequency?: number;
    brightness: number;
    decay: number;
    feedback: number;
    pluckPosition?: number;
    outputGain?: number;
    seedSuffix?: string;
  }
): CreatedKarplusStrongString {
  const now = input.now;
  const frequency = Math.max(24, options.frequency ?? input.frequency);
  const maxDelay = 1 / 24;
  const delay = context.createDelay(maxDelay);
  const feedbackGain = context.createGain();
  const dampingFilter = context.createBiquadFilter();
  const exciterFilter = context.createBiquadFilter();
  const output = context.createGain();
  const seed = hashSeed(input.instrumentId, input.variantId, input.noteLabel, input.ornament, options.seedSuffix);
  const pluck = createNoiseBurst(context, {
    duration: 0.035,
    attack: 0.001,
    decay: 1.6 + (options.pluckPosition ?? 0.4) * 1.8,
    gain: input.velocity * (options.outputGain ?? 0.34),
    filterType: "highpass",
    frequency: 120 + frequency * (0.7 + options.brightness * 1.4),
    q: 0.55,
    seed,
    tilt: options.brightness > 0.58 ? "bright" : "flat",
    startTime: now
  });
  const safeFeedback = clampFeedbackGain(options.feedback);

  delay.delayTime.setValueAtTime(clamp(1 / frequency, 0.001, maxDelay), now);
  feedbackGain.gain.setValueAtTime(safeFeedback, now);
  dampingFilter.type = "lowpass";
  dampingFilter.frequency.setValueAtTime(900 + options.brightness * 6200, now);
  dampingFilter.Q.setValueAtTime(0.55 + options.brightness * 0.65, now);
  exciterFilter.type = "highpass";
  exciterFilter.frequency.setValueAtTime(80 + frequency * 0.12, now);
  output.gain.value = 0.9;

  pluck.output.connect(exciterFilter);
  exciterFilter.connect(delay);
  delay.connect(dampingFilter);
  dampingFilter.connect(feedbackGain);
  feedbackGain.connect(delay);
  delay.connect(output);
  pluck.source.start(now);

  const releaseAt = now + Math.max(0.12, options.decay * 5);
  feedbackGain.gain.setTargetAtTime(MIN_GAIN, now + options.decay, Math.max(0.03, options.decay * 0.65));

  try {
    pluck.source.stop(releaseAt);
  } catch {
    // A short buffer may already have ended.
  }

  return {
    source: pluck.source,
    output,
    delay,
    feedbackGain,
    dampingFilter,
    exciterFilter,
    cleanupNodes: [output, delay, feedbackGain, dampingFilter, exciterFilter, ...pluck.cleanupNodes]
  };
}

export function safeStopSource(source: AudioScheduledSourceNode, when: number): void {
  try {
    source.stop(when);
  } catch {
    // stop() is idempotent from the caller's perspective.
  }
}

export function safeDisconnect(...nodes: Array<AudioNode | undefined>): void {
  for (const node of nodes) {
    if (!node) {
      continue;
    }

    try {
      node.disconnect();
    } catch {
      // Nodes may already be disconnected by stop cleanup.
    }
  }
}

export function createAdvancedVoice(
  input: AdvancedSynthesisInput,
  parts: {
    id?: string;
    gain: GainNode;
    output?: AudioNode;
    sources: AudioScheduledSourceNode[];
    cleanupNodes: AudioNode[];
    releaseSeconds?: number;
    debug?: Record<string, unknown>;
  }
): AdvancedVoiceHandle {
  let stopped = false;
  let disconnected = false;
  const output = parts.output ?? parts.gain;
  const releaseSeconds = parts.releaseSeconds ?? 0.45;
  const voice: AdvancedVoiceHandle = {
    id: parts.id ?? `${input.instrumentId}-${input.variantId ?? "default"}-${Date.now()}`,
    noteName: input.noteLabel,
    output,
    gain: parts.gain,
    sources: parts.sources,
    cleanupNodes: parts.cleanupNodes,
    debug: {
      advancedSynthesisEnabled: true,
      instrumentId: input.instrumentId,
      variantId: input.variantId,
      ...parts.debug
    },
    stop: (when = input.context.currentTime) => {
      if (stopped) {
        return;
      }

      stopped = true;
      releaseAudioParam(parts.gain.gain, input.context, releaseSeconds, when);

      for (const source of parts.sources) {
        safeStopSource(source, when + releaseSeconds + 0.08);
      }
    },
    disconnect: () => {
      if (disconnected) {
        return;
      }

      disconnected = true;
      safeDisconnect(output, parts.gain, ...parts.cleanupNodes);
    }
  };

  return voice;
}

export function connectDryWet(input: AudioNode, dryGain: GainNode, wetDestination: AudioNode): void {
  input.connect(dryGain);
  dryGain.connect(wetDestination);
}

export function copyVoiceAsAudioVoice(voice: AdvancedVoiceHandle): AudioVoice {
  return voice;
}
